import { Component, computed, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SystemAdminService, SystemAdminBusiness, MembershipPlanDto } from '../../../../services/system-admin/system-admin.service';
import { ToastService } from '../../../../shared/services/toast.service';

type ActionTab = 'info' | 'membership' | 'danger';

@Component({
  selector: 'app-business-actions',
  imports: [CommonModule, FormsModule],
  templateUrl: './business-actions.html',
})
export class BusinessActionsComponent implements OnInit {
  @Input({ required: true }) business!: SystemAdminBusiness;
  @Output() close = new EventEmitter<void>();
  @Output() actionCompleted = new EventEmitter<void>();

  private systemAdminService = inject(SystemAdminService);
  private toast = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  activeTab = signal<ActionTab>('info');
  loading = signal(false);

  // Membership form
  membershipAction = 'extend';
  extensionDays = 30;
  newEndDate = '';

  // Assign membership form
  membershipPlans = signal<MembershipPlanDto[]>([]);
  plansLoading = signal(false);
  selectedPlanId = '';
  assignEndDate = '';

  // Change plan form
  showChangePlan = signal(false);
  showChangePlanConfirm = signal(false);
  changePlanId = '';
  changeTransitionDate = '';
  changeNewEndDate = '';

  // Computed: available plans for change (exclude current plan)
  availablePlansForChange = computed(() => {
    const currentPlanId = this.business?.membershipPlanId;
    if (!currentPlanId) return this.membershipPlans();
    return this.membershipPlans().filter(p => p.membershipPlanId !== currentPlanId);
  });

  // Computed: selected plan name for confirmation
  get selectedChangePlanName(): string {
    const plan = this.membershipPlans().find(p => p.membershipPlanId === this.changePlanId);
    return plan ? `${plan.name} - $${plan.priceMonthly}/mes` : '';
  }

  // Danger zone
  showResetConfirm = signal(false);
  showDeleteConfirm = signal(false);
  resetConfirmText = '';
  deleteConfirmText = '';
  dangerLoading = signal(false);

  get hasMembership(): boolean {
    return this.business.membershipStatus !== 'Sin membresía';
  }

  ngOnInit(): void {
    this.loadMembershipPlans();
  }

  private loadMembershipPlans(): void {
    this.plansLoading.set(true);
    this.systemAdminService.getMembershipPlans().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (plans) => {
        this.membershipPlans.set(plans.filter(p => p.isActive));
        this.plansLoading.set(false);
      },
      error: () => {
        this.toast.show('Error al cargar planes de membresía', 'error');
        this.plansLoading.set(false);
      }
    });
  }

  formatDate(date: string | undefined): string {
    if (!date) return 'N/A';
    return formatDate(date, 'dd/MM/yyyy', 'es-CO');
  }

  // Assign Membership (for businesses without membership)
  onAssignMembership(): void {
    if (!this.selectedPlanId) {
      this.toast.show('Selecciona un plan', 'warning');
      return;
    }
    if (!this.assignEndDate) {
      this.toast.show('Selecciona una fecha de vencimiento', 'warning');
      return;
    }
    this.loading.set(true);
    this.systemAdminService.assignMembership(this.business.businessId, {
      membershipPlanId: this.selectedPlanId,
      endDate: this.assignEndDate
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.toast.show('Membresía asignada exitosamente', 'success');
        this.loading.set(false);
        this.actionCompleted.emit();
      },
      error: () => {
        this.toast.show('Error al asignar membresía', 'error');
        this.loading.set(false);
      }
    });
  }

  // Change Membership Plan
  onOpenChangePlan(): void {
    this.showChangePlan.set(true);
    this.showChangePlanConfirm.set(false);
    this.changePlanId = '';
    this.changeTransitionDate = '';
    this.changeNewEndDate = '';
  }

  onCancelChangePlan(): void {
    this.showChangePlan.set(false);
    this.showChangePlanConfirm.set(false);
  }

  onRequestChangePlan(): void {
    if (!this.changePlanId) {
      this.toast.show('Selecciona un nuevo plan', 'warning');
      return;
    }
    if (!this.changeTransitionDate) {
      this.toast.show('Selecciona la fecha de transición', 'warning');
      return;
    }
    if (!this.changeNewEndDate) {
      this.toast.show('Selecciona la fecha de vencimiento del nuevo plan', 'warning');
      return;
    }

    const transitionDate = new Date(this.changeTransitionDate);
    const newEndDate = new Date(this.changeNewEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (transitionDate >= newEndDate) {
      this.toast.show('La fecha de vencimiento debe ser posterior a la fecha de transición', 'warning');
      return;
    }

    // Show confirmation step
    this.showChangePlanConfirm.set(true);
  }

  onConfirmChangePlan(): void {
    this.loading.set(true);
    this.systemAdminService.changeMembershipPlan(this.business.businessId, {
      newMembershipPlanId: this.changePlanId,
      transitionDate: this.changeTransitionDate,
      newEndDate: this.changeNewEndDate
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.toast.show('Plan de membresía actualizado exitosamente', 'success');
        this.loading.set(false);
        this.showChangePlan.set(false);
        this.showChangePlanConfirm.set(false);
        this.actionCompleted.emit();
      },
      error: (err) => {
        const msg = err?.error?.message || 'Error al cambiar plan de membresía';
        this.toast.show(msg, 'error');
        this.loading.set(false);
        this.showChangePlanConfirm.set(false);
      }
    });
  }

  // Extend Membership
  onExtendMembership(): void {
    if (this.extensionDays < 1) {
      this.toast.show('Los días deben ser mayor a 0', 'warning');
      return;
    }
    this.loading.set(true);
    this.systemAdminService.extendMembership(this.business.businessId, { days: this.extensionDays }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.toast.show(`Membresía extendida por ${this.extensionDays} días`, 'success');
        this.loading.set(false);
        this.actionCompleted.emit();
      },
      error: () => {
        this.toast.show('Error al extender membresía', 'error');
        this.loading.set(false);
      }
    });
  }

  onSetMembershipEndDate(): void {
    if (!this.newEndDate) {
      this.toast.show('Selecciona una fecha', 'warning');
      return;
    }
    this.loading.set(true);
    this.systemAdminService.setMembershipEndDate(this.business.businessId, { endDate: this.newEndDate }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.toast.show('Fecha de vencimiento actualizada', 'success');
        this.loading.set(false);
        this.actionCompleted.emit();
      },
      error: () => {
        this.toast.show('Error al actualizar fecha', 'error');
        this.loading.set(false);
      }
    });
  }

  // Danger Zone
  onResetBusiness(): void {
    if (this.resetConfirmText !== this.business.name) {
      this.toast.show('El nombre no coincide', 'warning');
      return;
    }
    this.dangerLoading.set(true);
    this.systemAdminService.resetBusiness(this.business.businessId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.toast.show('Negocio reiniciado exitosamente', 'success');
        this.dangerLoading.set(false);
        this.showResetConfirm.set(false);
        this.resetConfirmText = '';
        this.actionCompleted.emit();
      },
      error: () => {
        this.toast.show('Error al reiniciar negocio', 'error');
        this.dangerLoading.set(false);
      }
    });
  }

  onDeleteBusiness(): void {
    if (this.deleteConfirmText !== 'ELIMINAR') {
      this.toast.show('Escribe ELIMINAR para confirmar', 'warning');
      return;
    }
    this.dangerLoading.set(true);
    this.systemAdminService.deleteBusiness(this.business.businessId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.toast.show('Negocio eliminado exitosamente', 'success');
        this.dangerLoading.set(false);
        this.showDeleteConfirm.set(false);
        this.deleteConfirmText = '';
        this.actionCompleted.emit();
      },
      error: () => {
        this.toast.show('Error al eliminar negocio', 'error');
        this.dangerLoading.set(false);
      }
    });
  }
}
